import { FC } from 'react';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { InfoOutlined, Link } from '@mui/icons-material';
import { Button, InputAdornment, Typography } from '@mui/material';

import DeactivatedSupplementMessage from '@components/DeactivatedSupplementMessage/DeactivatedSupplementMessage';
import FormSelect from '@components/FormSelect/FormSelect';
import FormTextField from '@components/FormTextField/FormTextField';
import RichContent from '@components/RichContent/RichContent';
import useSupplementCategories from '@store/hooks/useSupplementCategories';
import { getParsedText } from '@views/AdminInquiriesPage/AdminInquiryList/SupplementInquiryDetails/SupplementInformationForm/utils';
import removeWhiteSpaces from '@utils/removeWhiteSpaces';

import { ISupplementNeedsInfoFormProps } from './SupplementNeedsInfoForm.interface';

import './style.scss';

const SupplementNeedsInfoForm: FC<ISupplementNeedsInfoFormProps> = ({
  adminComments,
  handleSubmit,
  hasUnseenResponses,
  hasValidationErrors,
  isDeactivated,
  isLastOneThatNeedsInfo,
  onClose,
  submitting,
}) => {
  const { t } = useTranslation();

  const supplementCategories = useSupplementCategories();
  const submitLabel = isLastOneThatNeedsInfo
    ? t('inquiryResponse.resendInquiry')
    : t('common.save');

  return (
    <form className="supplement-needs-info-form" onSubmit={handleSubmit} noValidate>
      <Typography variant="h3" className="supplement-needs-info-form__title">
        {t('inquiryResponse.needsInfoTitle')}
      </Typography>

      {isDeactivated && (
        <DeactivatedSupplementMessage className="supplement-needs-info-form__deactivated" />
      )}

      <p className="supplement-needs-info-form__required small">{t('common.required')}</p>

      <Field
        component={FormTextField}
        className="supplement-needs-info-form__input"
        label={t('addSupplementModal.supplementName')}
        name="supplementName"
        maxRows={2}
        format={removeWhiteSpaces}
        formatOnBlur
        multiline
        required
      />

      <Field
        component={FormTextField}
        className="supplement-needs-info-form__input"
        label={t('addSupplementModal.companyName')}
        name="companyName"
        format={removeWhiteSpaces}
        formatOnBlur
        required
      />

      <Field
        component={FormSelect}
        className="supplement-needs-info-form__input"
        label={t('addSupplementModal.productCategory')}
        name="supplementCategoryId"
        options={supplementCategories}
      />

      <Field
        component={FormTextField}
        className="supplement-needs-info-form__input supplement-needs-info-form__link"
        label={t('addSupplementModal.linkToProductPage')}
        name="linkToProductPage"
        InputProps={{
          startAdornment: (
            <InputAdornment className="supplement-needs-info-form__input-icon" position="start">
              <Link color="inherit" />
            </InputAdornment>
          ),
        }}
        format={removeWhiteSpaces}
        formatOnBlur
        required
      />

      <div className="supplement-needs-info-form__admin-comments">
        {adminComments ? <RichContent>{getParsedText(adminComments)}</RichContent> : '-'}
      </div>

      <Field
        component={FormTextField}
        className="supplement-needs-info-form__input supplement-needs-info-form__note"
        label={t('inquiryResponse.needsInfoAddNote')}
        name="note"
        InputProps={{
          startAdornment: (
            <InputAdornment className="supplement-needs-info-form__input-icon" position="start">
              <img src="/images/img-note.svg" />
            </InputAdornment>
          ),
        }}
        rows={3}
        format={removeWhiteSpaces}
        formatOnBlur
        multiline
        required
      />

      {isLastOneThatNeedsInfo && hasUnseenResponses && (
        <p className="supplement-needs-info-form__has-unseen-responses">
          <InfoOutlined />
          {t('inquiryResponse.hasUnseenResponses')}
        </p>
      )}

      <div className="supplement-needs-info-form__controls">
        <Button
          className="supplement-needs-info-form__submit"
          type="submit"
          variant="contained"
          disabled={hasValidationErrors || submitting}
        >
          <img src="/images/img-submit.svg" />
          &nbsp;&nbsp;{submitLabel}
        </Button>

        <Button className="supplement-needs-info-form__cancel" variant="text" onClick={onClose}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
};

export default SupplementNeedsInfoForm;
